import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, RefreshCw, Receipt, ChevronDown } from 'lucide-react';
import { HIDE_GTPL } from '../utils/demo';
import CountUp from '../components/bits/CountUp';
import AnimatedContent from '../components/bits/AnimatedContent';

// App View (bottom-tab shell) layout of the Dashboard: three quick buttons, six key numbers in a
// 2×3 grid, a collapsed Customers dropdown, then one swipeable strip (Bandwidth, GTPL Wallet,
// Expiry, Today's Collection) switched by touch-swipe or by tapping a name. Same data the web
// Dashboard loads; Dashboard.js fetches it and hands it in.

const fmt = (n) => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));
const fmtWallet = (n) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const card = { background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12 };
const quickBtn = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3,
  minHeight: 58, padding: 4, fontSize: '.72rem', lineHeight: 1.15, textAlign: 'center', whiteSpace: 'normal',
};
const rowStyle = {
  display: 'flex', alignItems: 'center', gap: 10, minHeight: 50, padding: '0 14px',
  borderTop: '1px solid var(--border)', textDecoration: 'none', color: 'var(--text)', fontSize: '.84rem',
};
const head = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '10px 12px', fontWeight: 700, fontSize: '.88rem' };
const swipeCss = `.swipe-track{display:flex;overflow-x:auto;scroll-snap-type:x mandatory;scrollbar-width:none;-webkit-overflow-scrolling:touch;align-items:flex-start}.swipe-track::-webkit-scrollbar{display:none}.swipe-panel{flex:0 0 100%;min-width:0;scroll-snap-align:start;scroll-snap-stop:always}.swipe-panel .card{border:0;box-shadow:none;margin:0;border-radius:0;background:none}`;

// num + prefix count up (react-bits CountUp); plain `value` still works for text.
function Tile({ to, label, value, num, prefix = '', sub, color }) {
  return (
    <Link to={to} className="av-press" style={{ ...card, display: 'grid', gap: 1, padding: '10px 12px', textDecoration: 'none', color: 'var(--text)', minWidth: 0 }}>
      <span style={{ fontSize: '.64rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: '1.15rem', fontWeight: 800, color: color || 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {num != null ? <CountUp to={Math.round(num)} prefix={prefix} separator="," duration={1.2} trigger="load" /> : value}
      </span>
      <span style={{ fontSize: '.68rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</span>
    </Link>
  );
}

export default function AppDashboard({
  overview, paySummary, newTotal, leadTotal, isAdmin,
  gtplWallets, gtplSession, onRefreshWallets, walletsRefreshing, live,
}) {
  const [custOpen, setCustOpen] = useState(false);
  const [idx, setIdx] = useState(0);
  const track = useRef(null);
  const go = (i) => track.current?.scrollTo({ left: i * track.current.clientWidth, behavior: 'smooth' });
  const onScroll = (e) => setIdx(Math.round(e.currentTarget.scrollLeft / e.currentTarget.clientWidth));

  const today = paySummary?.today || {};
  const c = overview.customers;
  const nonCash = Math.max(0, (today.amount || 0) - (today.cash || 0));
  const growth = overview.revenue.growth;
  const wallets = gtplWallets?.entities || [];
  const maxWallet = Math.max(1, ...wallets.map(w => w.balance || 0));
  const sessionOk = gtplSession && gtplSession.configured && gtplSession.lastTestResult !== 'failed';

  const panels = [
    live && { key: 'bw', label: 'Bandwidth', body: live },
    isAdmin && gtplWallets && !HIDE_GTPL && {
      key: 'wallet', label: 'GTPL Wallet',
      body: (
        <div>
          <div style={{ ...head, fontWeight: 500 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {gtplSession && (
                <span title={sessionOk ? 'GTPL Saathi session is active' : 'GTPL Saathi session is not active'}
                  style={{ width: 10, height: 10, borderRadius: '50%', background: sessionOk ? 'var(--success)' : 'var(--danger)' }} />
              )}
              <span style={{ color: 'var(--text-muted)', fontSize: '.78rem' }}>{sessionOk ? 'Session active' : 'Session inactive'}</span>
            </span>
            <button type="button" className="btn btn-secondary btn-sm" onClick={onRefreshWallets} disabled={walletsRefreshing} style={{ minHeight: 34 }}>
              <RefreshCw size={13} className={walletsRefreshing ? 'spin' : undefined} /> {walletsRefreshing ? 'Reading…' : 'Refresh'}
            </button>
          </div>
          {wallets.map(w => {
            const low = w.balance != null && w.balance < gtplWallets.lowBalance;
            return (
              <div key={w.code} style={rowStyle} title={w.fetchedAt ? `as of ${new Date(w.fetchedAt).toLocaleString('en-IN')}` : 'not read yet'}>
                <span style={{ fontFamily: 'monospace', flex: 1, fontSize: '.8rem' }}>{w.code}</span>
                <span style={{ width: 70, height: 6, borderRadius: 9, background: 'var(--border)', overflow: 'hidden' }}>
                  <span style={{ display: 'block', height: '100%', width: `${Math.round(((w.balance || 0) / maxWallet) * 100)}%`, background: low ? 'var(--danger)' : 'var(--success)' }} />
                </span>
                <strong style={{ width: 86, textAlign: 'right', color: low ? 'var(--danger)' : undefined }}>
                  {w.balance != null ? `₹${fmtWallet(w.balance)}` : '—'}{w.error ? ' !' : ''}
                </strong>
              </div>
            );
          })}
        </div>
      ),
    },
    {
      key: 'expiry', label: 'Expiry',
      body: [
        ['Today', overview.renewals.today, 'var(--warning)'],
        ['Tomorrow', overview.renewals.tomorrow, 'var(--text)'],
        ['Expired', overview.renewals.expired, 'var(--danger)'],
      ].map(([label, val, color]) => (
        <Link key={label} to="/renewals" style={{ ...rowStyle, borderTop: 0 }}>
          <span style={{ flex: 1 }}>{label}</span>
          <strong style={{ color }}>{val}</strong>
        </Link>
      )),
    },
    {
      key: 'coll', label: "Today's Collection",
      body: [
        ['Broadband', `₹${fmt(today.broadband)}`],
        ['CableTV', `₹${fmt(today.catv)}`],
        ['Cash', `₹${fmt(today.cash)} · ${today.cashCount || 0} of ${today.count || 0}`],
        ['Non-cash (UPI, gateway, cheque, transfer)', `₹${fmt(nonCash)}`],
      ].map(([label, val]) => (
        <div key={label} style={{ ...rowStyle, borderTop: 0 }}>
          <span style={{ flex: 1, minWidth: 0, color: 'var(--text-muted)' }}>{label}</span>
          <strong>{val}</strong>
        </div>
      )),
    },
  ].filter(Boolean);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 10 }}>
      <style>{swipeCss}</style>
      <AnimatedContent trigger="mount" stagger={0.06} distance={14} duration={0.45} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6 }}>
        <Link to="/customers/add" className="btn btn-primary av-press" style={quickBtn}><Users size={17} />Add Customer</Link>
        <Link to="/renewals" className="btn btn-warning av-press" style={quickBtn}><RefreshCw size={17} />View Renewals</Link>
        <Link to="/payments/advance?new=1" className="btn btn-secondary av-press" style={quickBtn}><Receipt size={17} />Advance Receipt</Link>
      </AnimatedContent>

      <AnimatedContent trigger="mount" stagger={0.07} distance={18} duration={0.5} delay={0.1} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 6 }}>
        <Tile to="/payments" label="Collected today" num={today.amount || 0} prefix="₹" color="var(--success)"
          sub={`${today.count || 0} receipts · cash ₹${fmt(today.cash)}`} />
        <Tile to="/customers?customerType=catv&status=active&balance=due" label="Pending" num={overview.outstanding || 0} prefix="₹" color="var(--danger)"
          sub={`from ${overview.outstandingCustomers} customers`} />
        <Tile to="/renewals" label="Expire today" num={overview.renewals.today || 0} color="var(--warning)"
          sub={`${overview.renewals.tomorrow} tomorrow · ${overview.renewals.expired} expired`} />
        <Tile to="/complaints" label="Open complaints" num={overview.complaints.open || 0}
          sub={`${overview.complaints.critical} critical`} />
        <Tile to="/payments" label="This month" num={overview.revenue.thisMonth || 0} prefix="₹"
          sub={`${growth >= 0 ? '+' : ''}${growth}% vs last month`} />
        <Tile to="/connection-requests" label="New this month" num={newTotal || 0}
          sub={`${leadTotal ?? 0} open leads`} />
      </AnimatedContent>

      <div style={card}>
        <button type="button" onClick={() => setCustOpen(o => !o)} aria-expanded={custOpen}
          style={{ ...head, width: '100%', background: 'none', border: 0, cursor: 'pointer', color: 'var(--text)', fontFamily: 'inherit' }}>
          <span>Customers</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {c.total} <span style={{ fontWeight: 500, color: 'var(--text-muted)', fontSize: '.78rem' }}>· {c.active} active</span>
            <ChevronDown size={16} style={{ transition: 'transform .2s', transform: custOpen ? 'rotate(180deg)' : 'none', color: 'var(--text-muted)' }} />
          </span>
        </button>
        {custOpen && (
          <div style={{ padding: '0 12px 12px', display: 'grid', gap: 12 }}>
            {[
              ['By service', [
                ['Broadband', c.broadband, '/customers?customerType=broadband'],
                ['CableTV', c.catv, '/customers?customerType=catv'],
                ['Combo', c.linkedCombo ?? 0, '/link-customers'],
              ]],
              ['CableTV status', [
                ['Active', c.catvActive, '/customers?customerType=catv&status=active'],
                ['Inactive', c.catvInactive, '/customers?customerType=catv&status=inactive'],
                ['Suspended', c.catvSuspended, '/customers?customerType=catv&status=suspended'],
              ]],
            ].map(([title, items]) => (
              <div key={title}>
                <div style={{ fontSize: '.68rem', fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 6 }}>{title}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                  {items.map(([label, val, to]) => (
                    <Link key={label} to={to} style={{ display: 'grid', justifyItems: 'center', gap: 2, padding: '10px 4px', minHeight: 56, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 10, textDecoration: 'none', color: 'var(--text)' }}>
                      <strong style={{ fontSize: '1.1rem', lineHeight: 1.1 }}>{val}</strong>
                      <span style={{ fontSize: '.72rem', color: 'var(--text-muted)' }}>{label}</span>
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ ...card, overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 20, padding: '0 14px', borderBottom: '1px solid var(--border)', overflowX: 'auto', scrollbarWidth: 'none' }}>
          {panels.map((p, i) => (
            <button key={p.key} type="button" onClick={() => go(i)}
              style={{
                background: 'none', border: 0, borderBottom: `2px solid ${i === idx ? 'var(--primary)' : 'transparent'}`, marginBottom: -1,
                padding: '11px 0', fontFamily: 'inherit', fontSize: '.84rem', fontWeight: i === idx ? 700 : 500,
                color: i === idx ? 'var(--text)' : 'var(--text-muted)', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'color .2s, border-color .2s',
              }}>
              {p.label}
            </button>
          ))}
        </div>
        <div className="swipe-track" ref={track} onScroll={onScroll}>
          {panels.map(p => <div key={p.key} className="swipe-panel">{p.body}</div>)}
        </div>
      </div>
    </div>
  );
}
